import { useEffect, useState } from "react";

import { useAccount } from "../lib/account.ts";
import { supabase } from "../lib/supabase.ts";
import { RESET_COPY } from "./passwordReset.constants.ts";
import { refusePassword, type ResetView } from "./passwordReset.model.ts";

/**
 * Setting a new password from an emailed link.
 *
 * ## THE TOKEN IS NEVER TOUCHED BY THIS CODE, and the absence is the design
 *
 * The browser client is built with `detectSessionInUrl: true`, so the Supabase library
 * reads the recovery tokens out of the URL fragment itself, exchanges them, and hands
 * back **an ordinary session** — which `AccountProvider` is already watching. So this
 * controller asks one question: is there a session? If yes, `updateUser({ password })`
 * is all that remains.
 *
 * The rejected implementation needed a `tokenFromFragment` parser tolerant of three
 * shapes, a `useState` initialiser to capture the value before the first render, and a
 * `replaceState` to scrub the address bar afterwards — the last of those because a
 * bearer credential left in a URL is in the browser history and in any screen-share.
 * **None of it is here.** The library does the scrubbing; there is no token of ours to
 * leak; and the highest-risk function in the old design does not exist to be got wrong.
 *
 * ## WHY THIS SCREEN SITS OUTSIDE BOTH ROUTE GATES
 *
 * A recovery link MAKES a session, so `SignedOutOnly` would bounce every visitor who
 * arrived correctly — and `RequireSession` would bounce anybody whose link had expired,
 * sending them to sign in with the password they came here because they do not know.
 * The two gates would have to disagree about one address, so neither owns it. The
 * paragraph in `AppRoutes.tsx` says the same thing where the table is.
 *
 * ## IT DOES NOT SIGN OUT A SESSION IT FINDS, which is a reversal worth recording
 *
 * The rejected version signed out first and showed a "signing out the account already
 * open here…" state, answering OND-19's fifth point — a link opened on a laptop where a
 * colleague is still signed in. **That was right for a token ONDO judged and is wrong
 * here**, because with Supabase the session IS the credential: signing out would
 * destroy the very thing the link arrived to establish, and the screen would then
 * refuse itself. The colleague's session is already gone for a different reason —
 * consuming the recovery fragment replaces it — so the case the ticket worried about
 * resolves correctly without a step. What remains true is that the password change ends
 * that account's other sessions, which the screen says before it asks.
 */
export function useResetPasswordController() {
  const { session, loading } = useAccount();

  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Whether the wait for a session has been given up on.
   *
   * Needed because "no session yet" and "no session ever" are the same value. The
   * library resolves the fragment asynchronously, so a reader who arrived correctly has
   * no session for the first moments — and `loading` goes false the instant the initial
   * lookup finishes, which can be before the exchange completes. Refusing on `!loading
   * && !session` alone would show "this page needs the link from your email" to somebody
   * holding a perfectly good one, for a moment, which is the worst possible flicker.
   */
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (loading || session) return;

    /*
      A SHORT GRACE PERIOD RATHER THAN A RACE.

      One second is long enough for a local token exchange and short enough that
      somebody who typed the address in by hand is not left watching a spinner. The
      timer is cleared the moment a session arrives, because the effect re-runs when
      `session` changes — so the honest path never waits the full second.
    */
    const timer = setTimeout(() => {
      setGaveUp(true);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [loading, session]);

  const refusal = refusePassword(password, confirmation);

  const view: ResetView = done
    ? { kind: "done" }
    : saving
      ? { kind: "saving" }
      : session
        ? {
            kind: "setting",
            password,
            confirmation,
            canSubmit: refusal === null,
            error,
            // Only once something has been typed, so an untouched form is not scolded
            // for being empty — which "too-short" would otherwise do immediately.
            refusal: password === "" ? null : refusal,
          }
        : gaveUp
          ? { kind: "unusable" }
          : { kind: "opening" };

  async function submitPassword(): Promise<void> {
    if (saving || refusal !== null) return;

    setSaving(true);
    setError(null);

    const { error: failure } = await supabase.auth.updateUser({ password });

    if (failure) {
      // One sentence for every way this fails. A screen that guessed between "expired"
      // and "already used" would be inventing a distinction the server does not make,
      // and both have the same remedy.
      setError(RESET_COPY.failure);
      setSaving(false);
      return;
    }

    setSaving(false);
    setDone(true);
  }

  return {
    view,
    changePassword: setPassword,
    changeConfirmation: setConfirmation,
    submitPassword,
  };
}
